import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PageTitleService } from '../../core/services/page-title.service';
import { MarketingService } from '../../core/services/marketing.service';
import { AuthStateService } from '../../core/services/auth-state.service';
import { compressImageForUpload } from '../../shared/utils/image-compression';
import { HttpSrcDirective } from '../../shared/directives/http-src.directive';
import { environment } from '../../../environments/environment';

function buildYearMonthOptions() {
  const opts: Array<{ id: string; name: string }> = [];
  const now = new JstDate();
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  for (let i = 0; i < 12; i++) {
    year = month === 1 ? year - 1 : year;
    month = month === 1 ? 12 : month - 1;
    if (year > 2020 || (year === 2020 && month > 10)) {
      opts.push({
        id: `${year}-${String(month).padStart(2, '0')}`,
        name: `${year}年${month}月`,
      });
    }
  }
  return opts;
}

@Component({
    selector: 'app-marketing-flyer',
    imports: [FormsModule, HttpSrcDirective],
    template: `
<div class="animated fadeIn container-fluid">
  <div class="card">
    <div class="card-header">
      <span><i class="fa s-icon-list"></i> チラシ</span>
    </div>
    <div class="card-body">
      <form novalidate name="myForm" #myForm="ngForm">
      <div class="row">
        <div class="col-md-4">
          <div class="form-group row">
            <div class="col-md-12">
              <label class="col-form-label" for="year_pay">対象年月</label>
              <select class="form-control" id="year_pay" name="year_pay" [(ngModel)]="selectedYearMonth" (ngModelChange)="load()">
                @for (opt of yearMonthOptions; track opt.id) {
                  <option [ngValue]="opt.id">{{ opt.name }}</option>
                }
              </select>
            </div>
          </div>
        </div>
      </div>
      </form>
      <hr>
    </div>
    <div class="card-body">
      @if (userPermission <= 10) {
        <div class="form-group row">
          <div class="col-md-12">
            <button class="btn btn-success" type="button" data-toggle="modal" data-target="#imageUploadModalOne" [disabled]="!selectedYearMonth" (click)="openUploadModal(1)">
              <i class="fa s-icon-plus"></i>&nbsp;画像を追加：1枚目
            </button>
            <button class="btn btn-success ml-2" type="button" data-toggle="modal" data-target="#imageUploadModalTwo" [disabled]="!selectedYearMonth" (click)="openUploadModal(2)">
              <i class="fa s-icon-plus"></i>&nbsp;画像を追加：2枚目
            </button>
          </div>
        </div>
      }
      <div class="form-group row">
        <div class="col-md-6">
          @if (imageSrc1) {
            <img [httpSrc]="imageSrc1" class="img-thumbnail" id="uploaded-img1">
          }
        </div>
        <div class="col-md-6">
          @if (imageSrc2) {
            <img [httpSrc]="imageSrc2" class="img-thumbnail" id="uploaded-img2">
          }
        </div>
      </div>
    </div>
  </div>
</div>

@if (showUploadModal && uploadPictureId) {
  <div class="modal-backdrop fade show" (click)="showUploadModal = false"></div>
  <div class="modal fade show d-block" [id]="uploadPictureId === 1 ? 'imageUploadModalOne' : 'imageUploadModalTwo'" tabindex="-1" role="dialog" [attr.aria-labelledby]="uploadPictureId === 1 ? 'imageUploadModalOneLabel' : 'imageUploadModalTwoLabel'" aria-hidden="false">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" [id]="uploadPictureId === 1 ? 'imageUploadModalOneLabel' : 'imageUploadModalTwoLabel'">画像({{ uploadPictureId }}枚目)アップロード</h5>
          <button type="button" class="close" (click)="showUploadModal = false">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="input-group">
            <label class="input-group-btn">
              <span class="btn btn-light">
                <i class="fa s-icon-image"></i>&nbsp;ファイル選択
                <input type="file" file-model="$parent.myPicture" class="d-none" accept="image/*" (change)="onFileSelect($event)">
              </span>
            </label>
            <input type="text" class="form-control bg-white border-0" readonly [value]="selectedFile?.name ?? '選択されていません'">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showUploadModal = false">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-success" type="button" (click)="uploadImage()" [disabled]="!selectedFile">
            <i class="fa icon-arrow-up-circle"></i>&nbsp;アップロード
          </button>
        </div>
      </div>
    </div>
  </div>
}
  `
})
export class MarketingFlyerComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private marketingService = inject(MarketingService);
  private authState = inject(AuthStateService);

  get userPermission(): number { return this.authState.currentUser?.role ?? 99; }

  selectedYearMonth = '';
  flyer: any = null;
  imageSrc1 = '';
  imageSrc2 = '';
  showUploadModal = false;
  uploadPictureId: 1 | 2 | null = null;
  selectedFile: File | null = null;
  readonly yearMonthOptions = buildYearMonthOptions();

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('マーケティング', 'fa fa-area-chart');
    if (this.yearMonthOptions.length > 0) {
      this.selectedYearMonth = this.yearMonthOptions[0].id;
      await this.load();
    }
  }

  async load(): Promise<void> {
    if (!this.selectedYearMonth) return;
    this.imageSrc1 = '';
    this.imageSrc2 = '';
    this.flyer = null;
    try {
      const flyer = await this.marketingService.getFlyer(this.selectedYearMonth) as any;
      if (flyer) {
        this.flyer = flyer;
        this.imageSrc1 = flyer.uploadedImg1 ? `${environment.apiBaseUrl}/marketing/${flyer.id}/picture/${flyer.uploadedImg1}` : '';
        this.imageSrc2 = flyer.uploadedImg2 ? `${environment.apiBaseUrl}/marketing/${flyer.id}/picture/${flyer.uploadedImg2}` : '';
      }
    } catch { /* ignore */ }
  }

  openUploadModal(pictureId: 1 | 2): void {
    this.uploadPictureId = pictureId;
    this.selectedFile = null;
    this.showUploadModal = true;
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  async uploadImage(): Promise<void> {
    if (!this.flyer?.id || !this.uploadPictureId || !this.selectedFile) return;
    const compressedFile = await compressImageForUpload(this.selectedFile);
    const fd = new FormData();
    fd.append('file', compressedFile);
    await this.marketingService.uploadPicture(this.flyer.id, this.uploadPictureId, fd);
    this.showUploadModal = false;
    await this.load();
  }
}
